import { useState, useEffect } from 'react';
import { UserIdentity } from '../identity/types';
import { entityResolver, Channel } from './EntityResolver';
import { supabase } from '../../supabase';

export function useEntity(entityId: string | null, type: 'user' | 'channel' | 'server' = 'user') {
  const [entity, setEntity] = useState<any | null>(() => {
    if (!entityId) return null;
    if (type === 'user') return entityResolver.resolveUser(entityId);
    if (type === 'channel') return entityResolver.resolveChannel(entityId);
    if (type === 'server') return entityResolver.resolveServer(entityId);
    return null;
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!entityId) {
      setEntity(null);
      return;
    }

    let cached;
    if (type === 'user') cached = entityResolver.resolveUser(entityId);
    else if (type === 'channel') cached = entityResolver.resolveChannel(entityId);
    else if (type === 'server') cached = entityResolver.resolveServer(entityId);

    if (cached) {
      setEntity(cached);
      return;
    }

    setLoading(true);
    
    // Attempt real resolution or fallback for phase 1
    const fetchEntity = async () => {
      try {
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(entityId);

        if (type === 'user') {
          if (!isUuid) {
            setEntity({ id: entityId, name: entityId });
            return;
          }
          const { data } = await supabase.from('profiles').select('id, display_name, photo_url, customization, bio').eq('id', entityId).single();
          if (data) {
            const mappedUser: UserIdentity = {
              id: data.id,
              name: data.display_name || 'Unknown User',
              avatar: data.photo_url,
              customization: data.customization
            };
            entityResolver.updateUser(entityId, mappedUser);
            setEntity(mappedUser);
          } else {
             setEntity({ id: entityId, name: 'Unknown User' });
          }
        } else if (type === 'channel') {
           if (!isUuid) {
             const defaultChannel = { id: entityId, name: entityId };
             entityResolver.updateChannel(entityId, defaultChannel as any);
             setEntity(defaultChannel);
             return;
           }
           const { data } = await supabase.from('channels').select('id, name, type, customization, vault_id').eq('id', entityId).single();
           if (data) {
             entityResolver.updateChannel(entityId, data);
             setEntity(data);
           } else {
             setEntity({ id: entityId, name: entityId });
           }
        } else {
             setEntity({ id: entityId, name: entityId });
        }
      } catch (err) {
        setEntity({ id: entityId, name: entityId });
      } finally {
        setLoading(false);
      }
    };
    
    fetchEntity();
  }, [entityId, type]);

  return { entity, loading };
}
